import Link from 'next/link'

import type { GetStaticPaths, GetStaticProps } from 'next'
import type { FC, ReactNode } from 'react'
import type { Vehicle, VehicleSummary } from '@/types'

import {
    CalendarDays,
    Car,
    Check,
    ChevronRight,
    Clock,
    Fuel,
    Luggage,
    Users
} from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { buttonClass } from '@/lib/ui'
import { formatPrice, pluralize } from '@/lib/format'
import { getVehicleName, pickRelatedVehicles, toBookingVehicle } from '@/lib/vehicle-helpers'
import { getPublishedVehicles, getPublishedVehicleSlugs, getVehicleBySlug } from '@/lib/vehicles'
import { loadWithBuildFallback } from '@/lib/static-props'
import Eyebrow from '@/components/generals/eyebrow'
import RichText from '@/components/generals/rich-text'
import VehicleCard from '@/components/vehicles/vehicle-card'
import VehicleGallery from '@/components/vehicles/vehicle-gallery'
import BookingSection from '@/components/booking/booking-section'

interface VehiclePageProps {
    vehicle: Vehicle
    related: Array<VehicleSummary>
}

interface PageData {
    vehicle: Vehicle | null
    fleet: Array<VehicleSummary>
}

export const getStaticPaths: GetStaticPaths = async () => {
    const slugs = await loadWithBuildFallback('vehiculos/[slug]', getPublishedVehicleSlugs, [])

    return {
        paths: slugs.map(slug => ({ params: { slug } })),
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<VehiclePageProps, { slug: string }> = async ({ params }) => {
    const slug = params?.slug ?? ''

    const data = await loadWithBuildFallback<PageData>('vehiculos/[slug]', async () => {
        const [vehicle, fleet] = await Promise.all([getVehicleBySlug(slug), getPublishedVehicles()])

        return { vehicle, fleet }
    }, { vehicle: null, fleet: [] })

    if (!data.vehicle) return { notFound: true, revalidate: 60 }

    return {
        props: {
            vehicle: data.vehicle,
            related: pickRelatedVehicles(data.vehicle, data.fleet, 3)
        },
        revalidate: 60
    }
}

const VehiclePage: FC<VehiclePageProps> = ({ vehicle, related }): ReactNode => {
    const name = getVehicleName(vehicle)
    const galleryOffset = vehicle.imagen ? 2 : 1

    const images = [
        ...(vehicle.imagen ? [{ src: vehicle.imagen, alt: vehicle.imagen_alt || name }] : []),
        ...vehicle.galeria.map((src, index) => ({ src, alt: vehicle.galeria_alt[index] || `${name}, foto ${index + galleryOffset}` }))
    ]

    const specClass = 'flex items-start gap-3 rounded-2xl border border-border/70 bg-card/70 p-4'
    const specIconClass = 'flex size-10 shrink-0 items-center justify-center rounded-full bg-secondary text-gold'

    const marca = <span data-mdlfy-cms={`vehiculos:${vehicle.id}:marca`}>{vehicle.marca}</span>
    const modelo = <span data-mdlfy-cms={`vehiculos:${vehicle.id}:modelo`}>{vehicle.modelo}</span>

    return (
        <Layout>
            <Seo title={name} description={vehicle.resumen ?? undefined} ogImage={vehicle.imagen ?? undefined} />

            <section id='vehiculo' aria-labelledby='vehiculo-titulo' className='pt-8 pb-20 sm:pt-10 lg:pb-28'>
                <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
                    <nav aria-label='Ruta de navegación'>
                        <ol className='flex min-w-0 flex-wrap items-center gap-1.5 text-sm text-muted-foreground'>
                            <li>
                                <Link href='/' className='transition-colors hover:text-foreground'>
                                    Inicio
                                </Link>
                            </li>

                            <li aria-hidden='true'>
                                <ChevronRight className='size-3.5' />
                            </li>

                            <li>
                                <Link href='/#vehiculos' className='transition-colors hover:text-foreground'>
                                    Vehículos
                                </Link>
                            </li>

                            <li aria-hidden='true'>
                                <ChevronRight className='size-3.5' />
                            </li>

                            <li aria-current='page' className='truncate font-medium text-foreground'>
                                {marca} {modelo}
                            </li>
                        </ol>
                    </nav>

                    <div className='mt-8 grid gap-10 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:gap-14'>
                        <VehicleGallery images={images} categoria={vehicle.categoria} name={name} />

                        <div className='flex flex-col'>
                            <Eyebrow>
                                <span data-mdlfy-cms={`vehiculos:${vehicle.id}:categoria`}>{vehicle.categoria}</span>
                            </Eyebrow>

                            <h1 id='vehiculo-titulo' className='mt-5 font-heading leading-[1.05] text-foreground'>
                                <span className='block text-2xl text-muted-foreground sm:text-3xl'>{marca}</span>
                                <span className='mt-1 block text-5xl sm:text-6xl'>{modelo}</span>
                            </h1>

                            {vehicle.resumen && (
                                <p className='mt-5 text-base leading-relaxed text-pretty text-muted-foreground sm:text-lg' data-mdlfy-cms={`vehiculos:${vehicle.id}:resumen`}>
                                    {vehicle.resumen}
                                </p>
                            )}

                            <dl className='mt-8 grid grid-cols-2 gap-3'>
                                <div className={specClass}>
                                    <span className={specIconClass}>
                                        <Users className='size-4' aria-hidden='true' />
                                    </span>

                                    <div>
                                        <dt className='text-xs text-muted-foreground'>Capacidad</dt>
                                        <dd className='mt-0.5 text-sm font-semibold'>
                                            <span>{vehicle.pasajeros}</span> {pluralize(vehicle.pasajeros, 'pasajero', 'pasajeros')}
                                        </dd>
                                    </div>
                                </div>

                                {vehicle.maletas !== null && (
                                    <div className={specClass}>
                                        <span className={specIconClass}>
                                            <Luggage className='size-4' aria-hidden='true' />
                                        </span>

                                        <div>
                                            <dt className='text-xs text-muted-foreground'>Equipaje</dt>
                                            <dd className='mt-0.5 text-sm font-semibold'>
                                                <span>{vehicle.maletas}</span> {pluralize(vehicle.maletas, 'maleta', 'maletas')}
                                            </dd>
                                        </div>
                                    </div>
                                )}

                                {vehicle.motorizacion && (
                                    <div className={specClass}>
                                        <span className={specIconClass}>
                                            <Fuel className='size-4' aria-hidden='true' />
                                        </span>

                                        <div>
                                            <dt className='text-xs text-muted-foreground'>Motorización</dt>
                                            <dd className='mt-0.5 text-sm font-semibold'>
                                                <span data-mdlfy-cms={`vehiculos:${vehicle.id}:motorizacion`}>{vehicle.motorizacion}</span>
                                            </dd>
                                        </div>
                                    </div>
                                )}

                                {vehicle.horas_minimas !== null && (
                                    <div className={specClass}>
                                        <span className={specIconClass}>
                                            <Clock className='size-4' aria-hidden='true' />
                                        </span>

                                        <div>
                                            <dt className='text-xs text-muted-foreground'>Servicio mínimo</dt>
                                            <dd className='mt-0.5 text-sm font-semibold'>
                                                <span>{vehicle.horas_minimas}</span> {pluralize(vehicle.horas_minimas, 'hora', 'horas')}
                                            </dd>
                                        </div>
                                    </div>
                                )}
                            </dl>

                            <div className='mt-6 rounded-3xl border border-border/80 bg-card p-6 shadow-[0_24px_50px_-36px_rgb(40_30_10/0.45)]'>
                                {vehicle.precio_hora !== null
                                    ? (
                                        <>
                                            <p className='text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground'>
                                                Desde
                                            </p>

                                            <p className='mt-1 flex items-baseline gap-1.5'>
                                                <span className='font-heading text-5xl leading-none'>{formatPrice(vehicle.precio_hora)}</span>
                                                <span className='text-muted-foreground'>/hora</span>
                                            </p>
                                        </>
                                    )
                                    : (
                                        <p className='font-heading text-3xl'>
                                            Precio a consultar
                                        </p>
                                    )}

                                <div className='mt-6 flex flex-col gap-3 sm:flex-row lg:flex-col xl:flex-row'>
                                    <a href='#reservar' className={buttonClass('primary', 'lg', 'flex-1')}>
                                        <CalendarDays className='size-4' aria-hidden='true' />
                                        Reservar este vehículo
                                    </a>

                                    <Link href='/#vehiculos' className={buttonClass('outline', 'lg')}>
                                        <Car className='size-4' aria-hidden='true' />
                                        Ver la flota
                                    </Link>
                                </div>

                                <p className='mt-4 text-xs leading-relaxed text-muted-foreground'>
                                    El precio final depende del trayecto, la duración y la fecha. Te lo confirmamos antes de cerrar la reserva.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className='mt-16 grid gap-12 border-t border-border/70 pt-14 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:gap-14'>
                        <div>
                            <h2 className='font-heading text-3xl sm:text-4xl'>
                                Sobre este vehículo
                            </h2>

                            {vehicle.descripcion
                                ? <RichText html={vehicle.descripcion} className='mt-6 max-w-2xl text-base leading-relaxed text-foreground/80 sm:text-lg' />
                                : (
                                    <p className='mt-6 text-muted-foreground'>
                                        Muy pronto añadiremos más información sobre este vehículo.
                                    </p>
                                )}
                        </div>

                        <div className='space-y-12'>
                            {vehicle.equipamiento.length > 0 && (
                                <div>
                                    <h2 className='font-heading text-2xl sm:text-3xl'>
                                        Equipamiento a bordo
                                    </h2>

                                    <ul className='mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-1'>
                                        {vehicle.equipamiento.map(item => (
                                            <li key={item} className='flex items-start gap-3 text-sm text-foreground/85'>
                                                <span className='mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-secondary text-gold'>
                                                    <Check className='size-3.5' aria-hidden='true' />
                                                </span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {vehicle.ideal_para.length > 0 && (
                                <div>
                                    <h2 className='font-heading text-2xl sm:text-3xl'>
                                        Ideal para
                                    </h2>

                                    <ul className='mt-6 flex flex-wrap gap-2'>
                                        {vehicle.ideal_para.map(occasion => (
                                            <li key={occasion}>
                                                <Link
                                                    href={{ pathname: '/', query: { ocasion: occasion }, hash: 'vehiculos' }}
                                                    className='inline-flex h-9 items-center rounded-full border border-border bg-card px-4 text-sm text-foreground/85 transition-colors hover:border-gold/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                                >
                                                    {occasion}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <BookingSection
                vehicles={[toBookingVehicle(vehicle)]}
                lockedVehicle={toBookingVehicle(vehicle)}
                title={(
                    <>
                        Reserva el {marca} <em className='text-champagne italic'>{modelo}</em>
                    </>
                )}
                intro='Indícanos la fecha, la hora y el punto de recogida. Te confirmaremos la disponibilidad de este vehículo y el precio final antes de cerrar nada.'
            />

            {related.length > 0 && (
                <section id='otros-vehiculos' aria-labelledby='otros-vehiculos-titulo' className='py-20 sm:py-24'>
                    <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
                        <div className='flex flex-wrap items-end justify-between gap-6'>
                            <div>
                                <Eyebrow>
                                    Más opciones
                                </Eyebrow>

                                <h2 id='otros-vehiculos-titulo' className='mt-4 font-heading text-4xl sm:text-5xl'>
                                    Otros vehículos de la flota
                                </h2>
                            </div>

                            <Link href='/#vehiculos' className={buttonClass('outline')}>
                                Ver todos los vehículos
                            </Link>
                        </div>

                        <ul className='mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                            {related.map(item => (
                                <li key={item.id}>
                                    <VehicleCard vehicle={item} headingLevel='h3' />
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}
        </Layout>
    )
}

export default VehiclePage
